import { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowsClockwise, CheckCircle, CreditCard, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const PLAN_META = {
  base: { name: "Standard", monthly: "29€", commitment: "sans engagement" },
  base_yearly: { name: "Standard", monthly: "26,10€", commitment: "engagement annuel" },
  premium: { name: "Premium", monthly: "49€", commitment: "sans engagement" },
  premium_yearly: { name: "Premium", monthly: "44,10€", commitment: "engagement annuel" },
};

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await api.get("/billing/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

const ELEMENTS_APPEARANCE = {
  theme: "flat",
  variables: {
    colorPrimary: "#3A8FA5",
    colorBackground: "#FFFFFF",
    colorText: "#1E3A5F",
    colorTextSecondary: "#5F758F",
    colorDanger: "#B9502C",
    fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
    borderRadius: "6px",
    spacingUnit: "4px",
  },
  rules: {
    ".Label": { color: "#5F758F", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "500" },
    ".Input": { border: "1px solid #E1E9F0", backgroundColor: "#FFFFFF", padding: "10px 12px" },
    ".Input:focus": { borderColor: "#3A8FA5", boxShadow: "0 0 0 1px #3A8FA5" },
  },
};

function CardStep({ onDone, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErrorMsg(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements, redirect: "if_required",
      confirmParams: { return_url: window.location.origin + "/settings?tab=billing" },
    });
    if (error) { setErrorMsg(error.message || "Impossible d'enregistrer la carte"); setBusy(false); return; }
    if (!setupIntent || setupIntent.status !== "succeeded" || !setupIntent.payment_method) {
      setErrorMsg("Vérification en cours. Réessayez dans un instant."); setBusy(false); return;
    }
    try {
      await api.post("/billing/update-payment-method", { payment_method_id: setupIntent.payment_method });
      toast.success("Moyen de paiement mis à jour");
      onDone?.();
    } catch (err) {
      log.error("update payment method", err);
      setErrorMsg(err?.response?.data?.detail || "Enregistrement impossible");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Adresse de facturation</div>
        <AddressElement options={{ mode: "billing", display: { name: "organization" }, fields: { phone: "auto" } }} />
      </div>
      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Nouvelle carte bancaire</div>
        <PaymentElement options={{ layout: "tabs", wallets: { applePay: "auto", googlePay: "auto", link: "never" } }} />
      </div>
      {errorMsg && <div className="text-sm text-[#B9502C] bg-[#FBE8E1] border border-[#B9502C]/20 rounded p-2">{errorMsg}</div>}
      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={busy} className="rounded-[10px] font-semibold" data-testid="reactivate-card-back">
          <ArrowLeft size={16} className="mr-1.5" /> Retour
        </Button>
        <Button type="submit" disabled={!stripe || busy} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold transition-colors disabled:opacity-50" data-testid="reactivate-card-submit">
          <Lock size={16} className="mr-2" /> {busy ? "Enregistrement…" : "Enregistrer et revenir"}
        </Button>
      </div>
    </form>
  );
}

export default function ReactivateSubscriptionDialog({ open, onOpenChange, currentPlan, scheduledEndDate, defaultPaymentMethod, onCardUpdated, onReactivated }) {
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [step, setStep] = useState("confirm");
  const [stripeObj, setStripeObj] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const plan = PLAN_META[currentPlan];

  useEffect(() => {
    if (!open) { setTerms(false); setStep("confirm"); setClientSecret(null); }
  }, [open]);

  const openCardStep = useCallback(async () => {
    setCardLoading(true); setStep("update-card");
    try {
      const s = await getStripe(); setStripeObj(s);
      const { data } = await api.post("/billing/setup-intent", {});
      setClientSecret(data.client_secret);
    } catch (err) {
      log.error("setup intent init", err);
      toast.error(err?.response?.data?.detail || "Impossible d'initialiser le formulaire carte");
      setStep("confirm");
    } finally { setCardLoading(false); }
  }, []);

  const cardDone = useCallback(() => {
    setClientSecret(null); setStep("confirm"); onCardUpdated?.();
  }, [onCardUpdated]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Résiliation annulée — abonnement repris");
      onReactivated?.();
      onOpenChange(false);
    } catch (err) {
      log.error("reactivate", err);
      toast.error(err?.response?.data?.detail || "Impossible de reprendre l'abonnement");
    } finally { setBusy(false); }
  };

  if (!plan) return null;
  const inCardStep = step === "update-card";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="reactivate-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F] flex items-center gap-2">
            {inCardStep
              ? <><CreditCard size={22} weight="duotone" className="text-[#3A8FA5]" /> Modifier le moyen de paiement</>
              : <><ArrowsClockwise size={22} weight="duotone" className="text-[#3A8FA5]" /> Reprendre mon abonnement</>}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            {inCardStep
              ? "Enregistrez une nouvelle carte, elle sera utilisée pour le prochain prélèvement."
              : "Confirmez la reprise de votre abonnement — la résiliation programmée sera annulée."}
          </DialogDescription>
        </DialogHeader>

        {inCardStep ? (
          cardLoading || !stripeObj || !clientSecret ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-2">
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" />
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          ) : (
            <Elements stripe={stripeObj} options={{ clientSecret, appearance: ELEMENTS_APPEARANCE }}>
              <CardStep onDone={cardDone} onBack={() => setStep("confirm")} />
            </Elements>
          )
        ) : (
          <>
            <div className="border-2 border-[#3A8FA5]/40 bg-[#E6F1F5] rounded-[10px] p-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={22} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#1E3A5F]">
                    Formule {plan.name} — {plan.commitment} — {plan.monthly} / mois TTC
                  </p>
                  <p className="text-xs text-[#3A5A70] mt-1 leading-relaxed">
                    En confirmant, la résiliation prévue le <strong>{scheduledEndDate}</strong> sera annulée.
                    Votre abonnement reprendra son cycle de renouvellement normal.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-xs text-[#5F758F] leading-relaxed bg-white border border-[#E1E9F0] rounded-md p-3 flex items-center justify-between gap-3" data-testid="reactivate-card-info">
              <div className="flex items-center gap-2 min-w-0">
                <CreditCard size={16} weight="duotone" className="text-[#3A8FA5] shrink-0" />
                <span className="truncate">
                  {defaultPaymentMethod?.last4
                    ? <>Prélèvement sur votre carte <strong className="uppercase">{defaultPaymentMethod.brand}</strong> <strong>•••• {defaultPaymentMethod.last4}</strong></>
                    : "Aucune carte enregistrée"}
                </span>
              </div>
              <button
                type="button"
                onClick={openCardStep}
                disabled={busy}
                className="text-xs font-semibold text-[#3A8FA5] hover:text-[#164E5F] underline underline-offset-2 whitespace-nowrap transition-colors"
                data-testid="reactivate-switch-card"
              >
                {defaultPaymentMethod?.last4 ? "Changer de carte" : "Ajouter une carte"}
              </button>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-[#5F758F] leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[#C7DEE6] text-[#3A8FA5] focus:ring-[#3A8FA5]"
                data-testid="reactivate-terms"
              />
              <span>
                J'accepte les{" "}
                <a href="/cgv" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGV</a>,{" "}
                les <a href="/cgu" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGU</a>{" "}
                et la <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">politique de confidentialité</a> de Questiomed.
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-[10px] font-semibold" data-testid="reactivate-abort-btn">
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={busy || !terms}
                className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold transition-colors disabled:opacity-50"
                data-testid="reactivate-confirm-btn"
              >
                <ArrowsClockwise size={16} className="mr-2" />
                {busy ? "Reprise…" : "Confirmer la reprise"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
